import { cn } from '@/utils/cn'
import { getMessage } from '@/utils/messages'
import logo from '@/assets/vvslogo.png'

interface WelcomeModalProps {
  open: boolean
  onStart: () => void
  onSkip: () => void
}

/** First-login welcome prompt shown before the guided tour's spotlight/dimming begins. */
export function WelcomeModal({ open, onStart, onSkip }: WelcomeModalProps) {
  if (!open) return null

  return (
    <>
      {/* No onClick here — the prompt is sticky/modal: it only closes via Start or Skip,
          not by clicking outside. */}
      <div
        className="fixed inset-0 z-[950] bg-black/40 backdrop-blur-[2px] tour-overlay-fade"
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="welcome-title"
        aria-describedby="welcome-desc"
        className={cn(
          'fixed z-[951] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
          'w-full max-w-sm bg-[var(--surface)] border border-[var(--border)] overflow-hidden',
          // Mobile: same rounded-corner radius, subtle shadow, and fade-in used by the
          // header's profile dropdown, for visual consistency between the two header-launched
          // popups. Desktop keeps the larger radius/deeper shadow that suits a full dialog.
          'rounded-[var(--radius-sm)] shadow-[0_8px_24px_rgba(0,0,0,0.10)] animate-fade-in',
          'sm:rounded-[var(--radius)] sm:shadow-2xl',
          'p-6 text-center'
        )}
      >
        <div className="w-16 h-16 rounded-[10px] flex items-center justify-center mx-auto mb-4 bg-white shadow-sm">
          <img src={logo} alt="VVSI Logo" className="w-12 h-12 object-contain" />
        </div>

        <h2
          id="welcome-title"
          className="text-[16px] font-bold text-[var(--text)] mb-2"
          style={{ fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }}
        >
          {getMessage('tour.welcome.title')}
        </h2>
        <p
          id="welcome-desc"
          className="text-[12.5px] text-[var(--text-dim)] leading-relaxed mb-6"
        >
          {getMessage('tour.welcome.description')}
        </p>

        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={onStart}
            className={cn(
              'px-4 py-[9px] rounded-[var(--radius-sm)] text-white text-[12.5px] font-semibold',
              'hover:opacity-90 transition-all duration-150 border border-transparent'
            )}
            style={{ background: 'linear-gradient(135deg, var(--accent), #2563eb)' }}
          >
            {getMessage('tour.welcome.button_start')}
          </button>
          <button
            type="button"
            onClick={onSkip}
            className="text-[11.5px] font-semibold text-[var(--text-muted)] hover:text-[var(--error)] transition-colors duration-150 py-1"
          >
            {getMessage('tour.welcome.button_skip')}
          </button>
        </div>
      </div>
    </>
  )
}
